import { NODE_DEFAULTS, type NodeDef, type StageDef } from "@reagent/sim";
import type { RunState, UpgradeDef, UpgradeKind } from "./types";
import { ERA_1_POOL, type EraPoolDef } from "@reagent/content";
import { RESALE_RULE } from "./resale";
import { applyMarket, marketFor } from "./market";

/**
 * Permanent rig upgrades, bought between stages and applied to every stage
 * after.
 *
 * They are implemented as transforms of the StageDef rather than as engine
 * features, which is the whole reason this package needs no changes to the
 * simulation: a stage is plain data, so "the rig is better now" is expressible
 * as "hand the engine a slightly different stage". Nothing in the tick loop
 * knows the meta-layer exists.
 */
export const UPGRADES: readonly UpgradeDef[] = [
  {
    kind: "maxPower",
    name: "Substation Tap",
    blurb: "+1 kW every Play phase, for the rest of the run.",
    cost: 55,
    maxStacks: 4,
  },
  {
    kind: "insulatedRig",
    name: "Rig Insulation",
    blurb: "+2 critical temperature on every machine. Buys you two more firings.",
    cost: 70,
    maxStacks: 2,
  },
  {
    kind: "linedPipes",
    name: "Shop-Wide Lining",
    blurb: "Every plain glass line starts PTFE-lined. Corrosion stops mattering.",
    cost: 90,
    maxStacks: 1,
  },
  {
    kind: "widerVessels",
    name: "Wider Vessels",
    blurb: "+1 buffer on every machine. Backlog takes a turn longer to bite.",
    cost: 65,
    maxStacks: 2,
  },
  {
    kind: "biggerHand",
    name: "Second Clipboard",
    blurb: "Draw one more card each turn.",
    cost: 80,
    maxStacks: 2,
  },
];

export function upgradeDef(kind: UpgradeKind): UpgradeDef {
  const found = UPGRADES.find((u) => u.kind === kind);
  if (!found) throw new Error(`unknown upgrade: ${kind}`);
  return found;
}

export function stacksOf(run: RunState, kind: UpgradeKind): number {
  return run.upgrades.filter((u) => u === kind).length;
}

export function canBuyUpgrade(run: RunState, kind: UpgradeKind): boolean {
  return stacksOf(run, kind) < upgradeDef(kind).maxStacks;
}

/**
 * Builds the stage the player actually faces: the authored stage, plus this
 * run's upgrades, plus this run's deck.
 *
 * The stage's own `startingDeck` is deliberately discarded — in a run the deck
 * is the player's, carried from stage to stage. That is the difference between
 * playing a stage and playing a run.
 *
 * The run's market (spec 0036) is applied last, on the same seam.
 */
export function composeStage(base: StageDef, run: RunState, pool: EraPoolDef = ERA_1_POOL): StageDef {
  const power = stacksOf(run, "maxPower");
  const insulation = stacksOf(run, "insulatedRig") * 2;
  const vessels = stacksOf(run, "widerVessels");
  const hand = stacksOf(run, "biggerHand");
  const lined = stacksOf(run, "linedPipes") > 0;

  const nodes: NodeDef[] = base.nodes.map((node) => {
    const heats = node.kind !== "inputTank" && node.kind !== "productHopper" && node.kind !== "wasteBuyer" && node.kind !== "vent";
    const criticalTemp =
      insulation > 0 && heats
        ? (node.criticalTemp ?? NODE_DEFAULTS.criticalTemp) + insulation
        : node.criticalTemp;

    // Hoppers are already effectively bottomless; widening them would only
    // hide a backlog the player is supposed to see. Waste buyers collect
    // byproducts and are equally bottomless. Vents are also bottomless.
    const bufferCapacity =
      vessels > 0 && node.kind !== "productHopper" && node.kind !== "wasteBuyer" && node.kind !== "vent"
        ? (node.bufferCapacity ?? NODE_DEFAULTS.bufferCapacity) + vessels
        : node.bufferCapacity;

    // Spread conditionally: under exactOptionalPropertyTypes an absent field
    // and a field set to undefined are different things, and NodeDef means
    // "absent" to be "take the engine default".
    return {
      ...node,
      ...(criticalTemp === undefined ? {} : { criticalTemp }),
      ...(bufferCapacity === undefined ? {} : { bufferCapacity }),
    };
  });

  const upgraded: StageDef = {
    ...base,
    nodes,
    edges: lined
      ? base.edges.map((edge) =>
          (edge.material ?? "glass") === "glass" ? { ...edge, material: "ptfeLined" as const } : edge,
        )
      : base.edges,
    energyBudgetKw: base.energyBudgetKw + power,
    handSize: base.handSize + hand,
    // The setup budget is the player's whole credit balance plus the stage's own
    // grant: Phase 1 shopping draws on everything they have saved, not a fixed
    // per-turn refill like energyBudgetKw. The grant is a placeholder funding rule
    // (spec 0037 OQ2 (a), needs playtest): a run starts at 0 credits, so without
    // it stage 1 could never buy its mandatory contracts.
    setupBudgetCredits: run.credits + (base.setupBudgetCredits ?? 0),
    // Spec 0021: a run's dismantles sell back on the ladder, not at cost.
    resaleRule: RESALE_RULE,
    startingDeck: [...run.deck],
  };
  // Last, so the market scales whatever contract the upgrades left on the tank
  // (spec 0036); it reads the run seed only, never a stage or shop stream.
  return applyMarket(upgraded, marketFor(run.seed, pool));
}
